import { useRef, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { retryVideo, errText } from "@/lib/fbLabApi";

const STALL_MS = 3 * 60 * 1000;

// Per-video Cloudflare MP4 preparation inside the posting window; a video whose % hasn't moved for 3 minutes is shown as stalled.
export function VideoPrep({ hid, pkg, onRefresh }) {
  const seen = useRef({});
  const [busy, setBusy] = useState(null);
  const vids = (pkg?.media || []).filter((m) => m.kind === "video");
  if (!vids.length) return null;
  const now = Date.now();
  const retry = async (key) => {
    setBusy(key);
    try { await retryVideo(hid, key); delete seen.current[key]; toast.success("Cloudflare is preparing the video again"); onRefresh(); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(null); }
  };
  return (
    <ul data-testid="group-publish-videos" className="space-y-1 text-xs">
      {vids.map((v) => {
        const s = seen.current[v.key];
        if (!s || s.pct !== v.percent) { seen.current[v.key] = { pct: v.percent, at: now }; }
        const stalled = v.prep === "preparing" && s && s.pct === v.percent && now - s.at > STALL_MS;
        const failed = v.prep === "failed" || stalled;
        return (
          <li key={v.key} data-testid={`group-publish-video-${v.order}`} className="flex items-center gap-2">
            {v.ready ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : failed ? <AlertTriangle className="h-3.5 w-3.5 text-red-600 shrink-0" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-500 shrink-0" />}
            <span className="min-w-0 flex-1 truncate">#{v.order} {v.name}</span>
            <span data-testid={`group-publish-video-${v.order}-state`} className={failed ? "text-red-700" : v.ready ? "text-emerald-700" : "text-stone-600"}>
              {v.ready ? "Ready" : stalled ? `Stalled at ${v.percent}%` : v.prep === "failed" ? "Failed" : v.prep === "processing" ? "Processing" : `Preparing ${v.percent ?? 0}%`}
            </span>
            {failed && <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`group-publish-video-${v.order}-retry`} disabled={busy === v.key} onClick={() => retry(v.key)}><RotateCw className="h-3 w-3 mr-1" />Retry</Button>}
          </li>
        );
      })}
    </ul>
  );
}
